'use client';

import { useAuth } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import {
  createContext, useContext, useEffect, useMemo, useRef,
  type AnchorHTMLAttributes, type ReactNode, type RefObject,
} from 'react';
import {
  type CompanyStatisticsAttribution, type CompanyStatisticsEventInput,
  type CompanyStatisticsKind, type CompanyStatisticsSurface,
  type CompanyStatisticsListingAttribution,
} from '@ra-mr/types';
import {
  companyAnalyticsConsentAccepted, subscribeCompanyAnalyticsConsent, useCompanyAnalyticsConsent,
} from '@/lib/privacy/company-analytics-consent';

interface StatisticsVisit {
  record: (attribution: CompanyStatisticsAttribution, kind: CompanyStatisticsKind, surface: CompanyStatisticsSurface) => void;
}
const VisitContext = createContext<StatisticsVisit | null>(null);
const requests = new Set<AbortController>();
let consentGeneration = 0;

/** Stable IDs survive delivery retries. Withdrawal cancels requests and prevents every retry. */
const deliver = async (event: CompanyStatisticsEventInput): Promise<void> => {
  const generation = consentGeneration;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (!companyAnalyticsConsentAccepted() || generation !== consentGeneration) {
      return;
    }
    const controller = new AbortController();
    requests.add(controller);
    const timeout = window.setTimeout(() => { controller.abort(); }, 5000);
    try {
      const response = await fetch('/api/public/company-statistics/events', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', keepalive: true,
        signal: controller.signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: [event] }),
      });
      if (response.ok || response.status < 500) {
        return;
      }
    } catch {
      // Measurement must not affect public content or navigation.
    } finally {
      requests.delete(controller);
      window.clearTimeout(timeout);
    }
    if (attempt < 2 && companyAnalyticsConsentAccepted() && generation === consentGeneration) {
      await new Promise<void>((resolve) => { window.setTimeout(resolve, 1000 * (attempt + 1)); });
    }
  }
};

/** Query/filter/pagination changes retain the occurrence; a different pathname starts a new visit. */
export const CompanyStatisticsProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const { isLoaded } = useAuth();
  const consent = useCompanyAnalyticsConsent();
  useEffect(() => {
    return subscribeCompanyAnalyticsConsent(() => {
      if (!companyAnalyticsConsentAccepted()) {
        consentGeneration += 1;
        for (const controller of requests) {
          controller.abort();
        }
        requests.clear();
      }
    });
  }, []);
  const visit = useMemo<StatisticsVisit>(() => {
    const recorded = new Set<string>();
    const occurrences = new Map<string, string>();
    return {
      record: (attribution, kind, surface) => {
        if (!companyAnalyticsConsentAccepted() || typeof crypto.randomUUID !== 'function') {
          return;
        }
        const key = `${pathname}:${attribution.providerId}:${kind}:${surface}`;
        if (recorded.has(key)) {
          return;
        }
        recorded.add(key);
        const occurrenceKey = `${pathname}:${attribution.providerId}:${surface}`;
        const listing = kind === 'listing_impression' || kind === 'listing_click';
        let eventId = listing ? occurrences.get(occurrenceKey) : undefined;
        if (!eventId) {
          eventId = crypto.randomUUID();
          if (listing) {
            occurrences.set(occurrenceKey, eventId);
          }
        }
        if (kind === 'listing_click') {
          // The server creates the impression atomically if the click arrives first.
          recorded.add(`${pathname}:${attribution.providerId}:listing_impression:${surface}`);
        }
        void deliver({ eventId, ticket: attribution.ticket });
      },
    };
  }, [pathname]);
  return <VisitContext.Provider value={isLoaded && consent.accepted ? visit : null}>{children}</VisitContext.Provider>;
};

export const CompanyStatisticsProfileView = ({ attribution }: { attribution?: CompanyStatisticsAttribution }) => {
  const visit = useContext(VisitContext);
  useEffect(() => {
    if (!visit || !attribution) {
      return;
    }
    let timer: number | undefined;
    const update = () => {
      window.clearTimeout(timer);
      if (document.visibilityState === 'visible') {
        timer = window.setTimeout(() => {
          if (document.visibilityState === 'visible') {
            visit.record(attribution, 'profile_view', 'profile');
          }
        }, 1000);
      }
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', update);
    };
  }, [attribution, visit]);
  return null;
};

const useVisibleStatistics = (
  marker: RefObject<HTMLElement>, attribution: CompanyStatisticsAttribution | undefined,
  kind: CompanyStatisticsKind, surface: CompanyStatisticsSurface, selector?: string,
): void => {
  const visit = useContext(VisitContext);
  useEffect(() => {
    if (!visit || !attribution || !marker.current || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const target = (selector ? marker.current.closest(selector) : marker.current) ?? marker.current;
    let visible = false;
    let timer: number | undefined;
    const update = () => {
      window.clearTimeout(timer);
      if (visible && document.visibilityState === 'visible') {
        timer = window.setTimeout(() => {
          if (visible && document.visibilityState === 'visible' && target.getClientRects().length > 0) {
            visit.record(attribution, kind, surface);
          }
        }, 1000);
      }
    };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === target) {
          visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          update();
        }
      }
    }, { threshold: [0, 0.5] });
    observer.observe(target);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, [attribution, kind, marker, selector, surface, visit]);
};

/** Marker remains inside the actual rate row/card rather than its section heading. */
export const CompanyStatisticsRateView = ({ attribution, surface, children }: {
  attribution?: CompanyStatisticsAttribution; surface: CompanyStatisticsSurface; children: ReactNode;
}) => {
  const marker = useRef<HTMLSpanElement>(null);
  useVisibleStatistics(marker, attribution, 'rate_view', surface, '[data-company-statistics-rate]');
  return <span ref={marker}>{children}</span>;
};

export const useCompanyStatisticsListing = (attribution: CompanyStatisticsListingAttribution | undefined, surface: CompanyStatisticsSurface) => {
  const marker = useRef<HTMLElement>(null);
  const visit = useContext(VisitContext);
  useVisibleStatistics(marker, attribution?.impression, 'listing_impression', surface);
  const recordClick = () => {
    if (attribution) {
      visit?.record(attribution.click, 'listing_click', surface);
    }
  };
  return { marker, recordClick };
};

export const useCompanyStatisticsListingClick = () => {
  const visit = useContext(VisitContext);
  return (attribution: CompanyStatisticsListingAttribution | undefined, surface: CompanyStatisticsSurface): void => {
    if (attribution) {
      visit?.record(attribution.click, 'listing_click', surface);
    }
  };
};

export const CompanyStatisticsListingView = ({ attribution, surface }: {
  attribution?: CompanyStatisticsListingAttribution; surface: CompanyStatisticsSurface;
}) => {
  const marker = useRef<HTMLSpanElement>(null);
  useVisibleStatistics(marker, attribution?.impression, 'listing_impression', surface, '[data-company-statistics-listing]');
  return <span ref={marker} aria-hidden="true" />;
};

export const CompanyStatisticsContactLink = ({ attribution, kind, surface, onClick, onAuxClick, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & {
  attribution?: CompanyStatisticsAttribution;
  kind: 'website_click';
  surface: 'profile' | 'provider_rates';
}) => {
  const visit = useContext(VisitContext);
  return (
    <a {...props} onClick={(event) => {
      onClick?.(event);
      if (!event.defaultPrevented && event.button === 0 && attribution) {
        visit?.record(attribution, kind, surface);
      }
    }} onAuxClick={(event) => {
      onAuxClick?.(event);
      if (!event.defaultPrevented && event.button === 1 && attribution) {
        visit?.record(attribution, kind, surface);
      }
    }}>{children}</a>
  );
};
